/* eslint-disable eqeqeq */
import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import { useLearnosityExamAccess } from '@components/Component/LearnosityExams/ShowIfLearnosityExamFlagEnabled';
import { useGetLearnosityExamTestSessions, useGetTestSessionCompletionStatus } from '@components/Component/LearnosityExams/hooks';
import DashboardUpNextWidget from '../../components/DashboardSummary/DashboardUpNextWidget';
import {
  completedExamKeysFromLearnosity,
  notStartedExamKeysFromLearnosity,
  testletIdByExamKeyFromSessions,
} from '../../components/DashboardSummary/filterExamSessions';
import { selectors as SectionSubjectSelectors } from '../../reducers/sectionSubjects';
import { selectors as SectionScheduleSelectors, actions as SectionScheduleActions } from '../../reducers/sectionSchedule';
import { selectors as SectionReviewsSelectors, actions as SectionReviewsActions } from '../../reducers/sectionReviews';
import { selectors as UserSelectors } from '../../reducers/user';
import { selectors as SectionsSelectors, actions as SectionsActions } from '../../reducers/sections';
import { selectors as SectionUnitSelectors } from '../../reducers/sectionUnit';
import { actions as PracticeReportActions } from '../../reducers/practiceReport';
import { actions as PracticeActions, selectors as PracticeSelectors } from '../../reducers/practice';

const mapStateToProps = (state, { match }) => {
  const subjectIds = SectionSubjectSelectors.getSubjectIds(state);
  const scheduleList = SectionScheduleSelectors.getScheduleList(state);
  const isGetting = SectionScheduleSelectors.isGettingSectionSchedule(state);
  const sectionId = _get(match, 'params.sectionId', null);
  const sectionDetail = SectionsSelectors.getSectionListById(state, sectionId);
  const sessionList = scheduleList.filter(
    ({ subject, activity_type: activityType }) => subject && subjectIds.includes(subject) && activityType !== 2,
  );
  const sessionExam = scheduleList.filter(({ type }) => type === 'exams');
  const totalReviews = SectionReviewsSelectors.getTotalReview(state);

  const examList = [];
  sessionExam.forEach((event) => {
    const { title, type } = event;

    if (event.session_sittings_dates.length > 0) {
      const examSections = event.session_metadata?.sections || [];
      if (examSections.length > 0) {
        examSections.forEach((section) => {
          const sectionExam = {
            id: section.section_id,
            date: section.section_date,
            section_name: section.section_name,
            exam_id: event.id,
            title,
            type,
          };
          const examById = [...new Set(examList.map((exam) => exam.id))];
          if (!examById.includes(section.section_id)) {
            examList.push(sectionExam);
          }
        });
      }
    } else {
      const examSections = event.exam?.sections || [];
      if (examSections.length > 0) {
        examSections.forEach((section) => {
          const sectionExam = {
            id: section.id,
            date: event.date,
            section_name: section.name,
            exam_id: event.id,
            title,
            type,
          };
          const examById = [...new Set(examList.map((exam) => exam.id))];
          if (!examById.includes(section.section_id)) {
            examList.push(sectionExam);
          }
        });
      }
    }
  });

  const completedPracticeUnitIds = [];
  const allPracticeSession = sessionList.filter((session) => {
    if (session.type === 'lessons' && session.lesson === null) {
      return true;
    }
    return false;
  });

  allPracticeSession.forEach((practice) => {
    const { unit: unitId } = practice;
    const practiceSummary = SectionsSelectors.getPracticeSummaryOfUnit(state, unitId);
    const { assets = [] } = SectionUnitSelectors.getUnitById(state, unitId);
    const practiceReport = {};

    practiceSummary.forEach((summaryItem) => {
      const { asset_type: assetType } = summaryItem;
      if (typeof assetType === 'undefined') {
        return;
      }

      const currentValueOfReport = _get(practiceReport, assetType, { count: 0 });
      if (currentValueOfReport.count > 0) {
        return;
      }
      practiceReport[assetType] = { count: currentValueOfReport.count + 1 };
    });
    if (practiceReport[null]) {
      delete practiceReport[null];
    }
    if (Object.values(practiceReport).length === assets.length) {
      completedPracticeUnitIds.push(unitId);
    }
  });

  return {
    sectionDetail,
    totalReviews,
    sessionList,
    examList,
    isGetting,
    subjectById: SectionSubjectSelectors.getSubjectById(state),
    userRole: UserSelectors.getUserRole(state),
    examsScore: SectionsSelectors.examsScoreObject(state),
    allPracticeSession,
    completedPracticeUnitIds,
    practiceId: PracticeSelectors.getPracticeId(state),
    isInitialized: PracticeSelectors.isInitialized(state),
    unitBySubject: SectionUnitSelectors.getUnitBySubject(state),
  };
};

const mapDispatchToProps = {
  sectionScheduleGet: SectionScheduleActions.sectionScheduleGet,
  sectionReviewSummary: SectionReviewsActions.sectionReviewSectionSummaryGet,
  sectionStudentReviewSummary: SectionReviewsActions.sectionStudentReviewSectionSummaryGet,
  sectionsExamsScoresGet: SectionsActions.sectionsExamsScoresGet,
  practiceReportGetDetail: PracticeReportActions.practiceReportGetDetail,
  initializePracticeSession: PracticeActions.initializePracticeSession,
};

const ConnectedDashboardUpNextWidget = connect(mapStateToProps, mapDispatchToProps)(DashboardUpNextWidget);

const DashboardUpNextWidgetContainer = (props) => {
  const { sectionId: sectionIdProp } = props;
  const isLearnosityExam = useLearnosityExamAccess();
  const sectionId = sectionIdProp ? +sectionIdProp : 0;
  const { data: testSessions } = useGetLearnosityExamTestSessions(sectionId || undefined, {
    suspense: false,
    enabled: Boolean(isLearnosityExam && sectionId),
  });
  const { data: completionStatus } = useGetTestSessionCompletionStatus(isLearnosityExam ? sectionId : 0);
  const learnosityExamsReady = !isLearnosityExam || (testSessions != null && completionStatus != null);
  const completedExamKeys = useMemo(
    () => (isLearnosityExam ? completedExamKeysFromLearnosity(testSessions, completionStatus) : null),
    [isLearnosityExam, testSessions, completionStatus],
  );
  const notStartedExamKeys = useMemo(
    () => (isLearnosityExam ? notStartedExamKeysFromLearnosity(testSessions, completionStatus) : null),
    [isLearnosityExam, testSessions, completionStatus],
  );
  const testletIdByExamKey = useMemo(
    () => (isLearnosityExam ? testletIdByExamKeyFromSessions(testSessions) : undefined),
    [isLearnosityExam, testSessions],
  );

  return (
    <ConnectedDashboardUpNextWidget
      {...props}
      completedExamKeys={completedExamKeys}
      notStartedExamKeys={notStartedExamKeys}
      learnosityExamsReady={learnosityExamsReady}
      testletIdByExamKey={testletIdByExamKey}
    />
  );
};

export default DashboardUpNextWidgetContainer;
